import React from 'react';
import { PermissionSection } from '../CommonComponents';
import { PERMISSIONS_TAB_HOOKS } from '../../../../../utils/dataHooks';

import type {
  OpenInstallPricingPlansModalProps,
  PageGroupData,
  RolesPanelProps,
} from '../../permissionsTabMapper';
import { isPansGroup, type Group } from '../../../../../utils/groupsApi';

export interface PicingPlansSectionProps {
  plansForPage: Required<Group>[];
  availablePlans: Required<Group>[];
  permissions?: string[];
  disabled: boolean;
  isPricingPlansAppInstalled: boolean;
  openRolesPanel: (panelProps: RolesPanelProps) => void;
  openDashboardModal: (path: string) => Promise<PageGroupData>;
  updateGroupsPermissions: (groupsToUpdate: Required<Group>[]) => Promise<void>;
  openInstallPricingPlansModal: (
    props: OpenInstallPricingPlansModalProps,
  ) => void;
}

interface OpenPlansPanelProps {
  plansForPage: Required<Group>[];
  availablePlans: Required<Group>[];
  onAddNewClick: () => void;
}

export const PricingPlansSection: React.FC<PicingPlansSectionProps> = ({
  permissions,
  plansForPage,
  disabled,
  availablePlans,
  isPricingPlansAppInstalled,
  openRolesPanel,
  openDashboardModal,
  updateGroupsPermissions,
  openInstallPricingPlansModal,
}) => {
  const hasCreatePlanGroup = availablePlans.length > 0;
  const canCreatePlans = permissions?.includes('paid-plans.manage');

  const openPlansDashboard = () =>
    openDashboardModal(
      '/pricing-plans/new?referralInfo=editor_page_permissions&viewMode=editor',
    );

  const openPaidPlansPanel = (props: OpenPlansPanelProps) => {
    openRolesPanel({
      panelName: 'panels.toolPanels.rolesManagerPanel',
      groups: props.availablePlans,
      selectedGroups: props.plansForPage,
      headerTitle: 'NewPages_Plans_Manager_Header_Title',
      applyButtonLabel: 'NewPages_Plans_Manager_Apply_Button',
      applyButtonCallback: updateGroupsPermissions,
      addNewButtonLabel: 'NewPages_Plans_Manager_Add_New_Plan',
      addNewButtonCallback: props.onAddNewClick,
      disableAddNew: !canCreatePlans,
      origin: 'pricing-plans-section',
    });
  };

  const disconnectPlan = async () => {
    const rolesToRemove = plansForPage.map((role) => ({
      ...role,
      checked: false,
    }));

    await updateGroupsPermissions(rolesToRemove);
  };

  const startPlanCreationFlow = async () => {
    const { groupsForPage, availableGroups } = await openPlansDashboard();
    const plansForPage = groupsForPage.filter(isPansGroup);
    const availablePlans = availableGroups.filter(isPansGroup);

    openPaidPlansPanel({
      plansForPage,
      availablePlans,
      onAddNewClick: openPlansDashboard,
    });
  };

  const pricingPlansInstallation = () =>
    openInstallPricingPlansModal({
      onAfterInstall: startPlanCreationFlow,
    });

  const handleCreatePlan = () => {
    if (isPricingPlansAppInstalled) {
      return openPlansDashboard();
    }
    return pricingPlansInstallation();
  };

  const handleConnectPlan = () => {
    if (isPricingPlansAppInstalled) {
      return openPaidPlansPanel({
        plansForPage,
        availablePlans,
        onAddNewClick: openPlansDashboard,
      });
    }

    return openPaidPlansPanel({
      plansForPage,
      availablePlans,
      onAddNewClick: pricingPlansInstallation,
    });
  };

  return (
    <PermissionSection
      dataHook={PERMISSIONS_TAB_HOOKS.PLANS_PERMISSION_SECTION}
      title="NewPages_Panel_Permissions_Plans_Section_Title"
      subtitle="NewPages_Panel_Permissions_Plans_Section_Subtitle"
      disabled={disabled}
      disconnectGroups={disconnectPlan}
      hasAvailableGroups={hasCreatePlanGroup}
      createPlaceholder="NewPages_Panel_Permissions_Plans_Section_Create_Placeholder"
      createText="NewPages_Panel_Permissions_Plans_Section_Create_Button"
      canCreateNew={canCreatePlans}
      onCreate={handleCreatePlan}
      connectedGroups={plansForPage}
      connectText="NewPages_Panel_Permissions_Plans_Section_Manage_Button"
      connectPlaceholder="NewPages_Panel_Permissions_Plans_Section_Manage_Placeholder"
      onConnect={handleConnectPlan}
    />
  );
};
